<template>
	<Transition :css="false" @enter="onEnter" @leave="onLeave">
		<div v-if="show" class="modal-overlay" @click="handleOverlayClick">
			<div class="modal-content" :class="contentClass" @click.stop ref="modalEl">
				<AppIcon v-if="appIcon" :name="appIcon" class="modal-bg-icon"/>
				<div v-if="showHeader" class="modal-header">
					<h3 v-if="title" class="modal-title">{{ title }}</h3>
				</div>
				
				<div class="modal-body">
					<slot></slot>
				</div>
				
				<div v-if="showFooter" class="modal-footer">
					<slot name="footer">
						<button v-if="cancelText" class="btn-secondary" @click="$emit('cancel')">
							{{ cancelText }}
						</button>
						<Transition name="confirm-expand">
							<button v-if="confirmText !== 'hidden'" class="btn-primary" :class="confirmButtonClass"
									@click="$emit('confirm')"
									:disabled="confirmDisabled">
								<Icon v-if="confirmDisabled && showLoadingIcon" name="loading" size="xs"
									  class="btn-icon spinning"/>
								{{ confirmDisabled && loadingText ? loadingText : confirmText }}
							</button>
						</Transition>
					</slot>
				</div>
			</div>
		</div>
	</Transition>
</template>

<script setup>
import Icon from './Icon.vue'
import {gsap} from 'gsap'
import AppIcon from "./AppIcon.vue";
import {ref} from "vue";

const props = defineProps({
	show: {type: Boolean, default: false},
	title: {type: String, default: ''},
	showHeader: {type: Boolean, default: true},
	showFooter: {type: Boolean, default: true},
	showCloseButton: {type: Boolean, default: true},
	cancelText: {type: String, default: 'Cancel'},
	confirmText: {type: String, default: 'Confirm'},
	loadingText: {type: String, default: ''},
	showLoadingIcon: {type: Boolean, default: false},
	confirmButtonClass: {type: String, default: ''},
	confirmDisabled: {type: Boolean, default: false},
	contentClass: {type: String, default: ''},
	closeOnOverlay: {type: Boolean, default: true},
	appIcon: {type: String, default: ''}
})

const emit = defineEmits(['close', 'cancel', 'confirm'])

function handleOverlayClick() {
	if (props.closeOnOverlay) {
		emit('close')
	}
}

const modalEl = ref(null)

function onEnter(el, done) {
	const overlay = el
	// const modal = modalEl.value
	const modal = el.querySelector('.modal-content')
	
	gsap.fromTo(overlay,
			{opacity: 0},
			{opacity: 1, duration: 0.25, ease: 'power2.out'}
	)
	gsap.fromTo(modal,
			{y: 40, opacity: 0},
			{y: 0, opacity: 1, duration: 0.35, ease: 'power3.out', onComplete: done}
	)
}

function onLeave(el, done) {
	const overlay = el
	// const modal = modalEl.value
	const modal = el.querySelector('.modal-content')
	
	gsap.to(overlay, {opacity: 0, duration: 0.25, ease: 'power2.in', delay: 0.05})
	gsap.to(modal,
			{y: 40, opacity: 0, duration: 0.25, ease: 'power3.in', onComplete: done}
	)
}
</script>

<style scoped>
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	overflow: hidden;
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
	padding: var(--spacing-lg);
}

.modal-overlay::before {
	content: '';
	position: absolute;
	inset: 0;
	backdrop-filter: blur(var(--blur-sm));
	-webkit-backdrop-filter: blur(var(--blur-sm));
	z-index: -1;
}

.modal-content {
	background: var(--color-popup-dark-2);
	backdrop-filter: blur(var(--blur-lg));
	-webkit-backdrop-filter: blur(var(--blur-lg));
	border: var(--border-x-thin) var(--color-border-light);
	border-radius: var(--border-radius-lg);
	width: 88vw;
	max-width: 500px;
	max-height: 90dvh;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	box-shadow: 0 0 0 100vmax rgba(0, 0, 0, 0.7);
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: var(--spacing-lg);
	border-bottom: var(--border-thin) var(--color-border);
}

.modal-title {
	margin: 0;
	width: 100%;
	text-align: center;
	color: var(--color-text);
	font-size: var(--font-size-lg);
	font-weight: var(--font-weight-bold);
}

.modal-bg-icon {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	z-index: 0;
	border-radius: var(--border-radius-lg);
	overflow: hidden;
	opacity: 0.3;
	filter: blur(var(--blur-lg));
}

.modal-header,
.modal-body,
.modal-footer {
	position: relative;
	z-index: 1;
}

.modal-close {
	background: none;
	border: none;
	color: var(--color-text-secondary);
	font-size: 1.2rem;
	cursor: pointer;
	padding: var(--spacing-sm);
	border-radius: var(--border-radius-sm);
	transition: all var(--transition-fast);
}

.modal-close:hover {
	background: var(--color-popup-element);
}

.modal-body {
	padding: var(--spacing-lg);
	overflow-y: auto;
	flex: 1;
}

.modal-footer {
	display: flex;
	gap: var(--spacing-md);
	padding: var(--spacing-lg);
	border-top: var(--border-thin) var(--color-border);
}

.btn-primary,
.btn-secondary {
	flex: 1;
	padding: var(--spacing-md) var(--spacing-lg);
	border: none;
	border-radius: var(--border-radius-md);
	font-size: var(--font-size-base);
	font-weight: var(--font-weight-medium);
	cursor: pointer;
	transition: all var(--transition-fast);
	display: flex;
	align-items: center;
	justify-content: center;
	gap: var(--spacing-xs);
}

.btn-primary {
	background: var(--color-primary);
	color: white;
}

.btn-primary:hover:not(:disabled) {
	background: var(--color-primary-hover);
}

.btn-primary.danger {
	background: var(--color-error);
}

.btn-primary.danger:hover:not(:disabled) {
	background: var(--color-error);
}

.btn-secondary {
	background: var(--color-popup-element);
	color: var(--color-text);
}

.btn-secondary:hover:not(:disabled) {
	background: var(--color-popup-light);
}

.btn-primary:disabled,
.btn-secondary:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.btn-icon.spinning {
	animation: spin 1s linear infinite;
}

@keyframes spin {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}

.confirm-expand-enter-active {
	transition: max-width 0.25s cubic-bezier(0.4, 0, 0.2, 1),
	padding 0.25s cubic-bezier(0.4, 0, 0.2, 1),
	transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
	opacity 0.2s ease;
	max-width: 500px;
	overflow: hidden;
	white-space: nowrap;
}

.confirm-expand-leave-active {
	transition: max-width 0.2s cubic-bezier(0.4, 0, 0.2, 1),
	padding 0.2s cubic-bezier(0.4, 0, 0.2, 1),
	transform 0.2s ease-in,
	opacity 0.15s ease;
	max-width: 500px;
	overflow: hidden;
	white-space: nowrap;
}

.confirm-expand-enter-from,
.confirm-expand-leave-to {
	opacity: 0;
	max-width: 0 !important;
	padding-left: 0 !important;
	padding-right: 0 !important;
	transform: translateX(24px);
}
</style>